/*
This computer program, as defined in the Copyright, Designs and Patents Act 1998 and the Software Directive (2009/24/EC), 
is the copyright of Logic Valley Ltd, a wholly owned subsidiary of Marston (Holdings) Ltd. All rights are reserved.
*/
import React, { useState, useMemo } from 'react'
import { useRecoilValue } from 'recoil'
import ImageViewer from '../ImageViewer'
import ImageThumbnailList from '../ImageThumbnailList'
import './ContraventionSnapshot.css'
import { timezoneAtom } from '@components/recoil/atoms/atoms'
import utils from '@utils/utils'
import { CONTRAVENTION_SNAPSHOT_CONSTANTS } from '@pages/manualReviewTab/constants'

const ContraventionSnapshot = ({ observationDetails = {} }) => {
  const [activeImageIndex, setActiveImageIndex] = useState(0)
  const timezoneData = useRecoilValue(timezoneAtom)
  const timezoneId = timezoneData?.selectedContract?.timezoneId

  // Extract and format images from observationDetails attachments
  const { entryImages, exitImages, allImages } = useMemo(() => {
    const attachments = observationDetails?.attachments || []
    const entry = attachments
      .filter(
        (att) =>
          att.vehicleDirection ===
          CONTRAVENTION_SNAPSHOT_CONSTANTS?.VEHICLE_DIRECTION?.ENTRY
      )
      .map((att) => ({
        id: att.id,
        name: att.fileName,
        url: att.fileLocation,
        timestamp: '',
        type: CONTRAVENTION_SNAPSHOT_CONSTANTS.IMAGE_TYPE.ENTRY,
      }))
    const exit = attachments
      .filter(
        (att) =>
          att.vehicleDirection ===
          CONTRAVENTION_SNAPSHOT_CONSTANTS?.VEHICLE_DIRECTION?.EXIT
      )
      .map((att) => ({
        id: att.id,
        name: att.fileName,
        url: att.fileLocation,
        timestamp: '',
        type: CONTRAVENTION_SNAPSHOT_CONSTANTS?.IMAGE_TYPE?.EXIT,
      }))
    return {
      entryImages: entry,
      exitImages: exit,
      allImages: [...entry, ...exit],
    }
  }, [observationDetails])

  const transitTimes = useMemo(() => {
    const entry = observationDetails?.transitStartTime
    const exit = observationDetails?.transitEndTime

    if (!entry && !exit) return ''

    const formattedEntry = entry
      ? utils?.convertUtcDateTimeToTimezoneDateTime(timezoneId, entry)
      : '-'
    const formattedExit = exit
      ? utils?.convertUtcDateTimeToTimezoneDateTime(timezoneId, exit)
      : '-'

    return `Entry : ${formattedEntry} | Exit : ${formattedExit}`
  }, [
    observationDetails?.transitStartTime,
    observationDetails?.transitEndTime,
    timezoneId,
  ])

  const contraventionNote =
    observationDetails?.offenceDetails?.contraventionNote || ''

  return (
    <>
      <div className="snapshot-header">
        {CONTRAVENTION_SNAPSHOT_CONSTANTS?.TITLE}
      </div>
      <div className="contravention-snapshot-container">
        <div className="snapshot-layout">
          <div className="image-viewer-section">
            {contraventionNote && (
              <div className="contravention-note-label">
                CN : {contraventionNote}
              </div>
            )}
            <ImageViewer
              images={allImages}
              currentIndex={activeImageIndex}
              onChangeIndex={setActiveImageIndex}
              transitTimes={transitTimes}
            />
          </div>

          <ImageThumbnailList
            entryImages={entryImages}
            exitImages={exitImages}
            onSelect={(id) =>
              setActiveImageIndex(allImages?.findIndex((img) => img.id === id))
            }
            activeId={allImages?.[activeImageIndex]?.id}
          />
        </div>
      </div>
    </>
  )
}

export default ContraventionSnapshot
